import React, { useEffect, useMemo, useState } from "react";
import PageHeader from "@/components/PageHeader";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { BookOpen, Plus, Search, Sparkles, Pencil, Trash2, Send, Tag } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";

const CATEGORIES = ["general", "pension", "cotisation", "prestations", "carte", "employeur", "technique", "procedure", "faq", "autre"];

export default function BaseConnaissances() {
  const { user } = useAuth();
  const canWrite = ["admin", "directeur", "chef", "superviseur"].includes(user?.role);
  const [rows, setRows] = useState([]);
  const [q, setQ] = useState("");
  const [filterCat, setFilterCat] = useState("all");
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);
  const emptyForm = { title: "", content: "", categorie: "general", tags_text: "", published: true };
  const [form, setForm] = useState(emptyForm);

  // AI ask panel
  const [askOpen, setAskOpen] = useState(false);
  const [question, setQuestion] = useState("");
  const [answer, setAnswer] = useState(null);
  const [asking, setAsking] = useState(false);

  const load = async () => {
    const params = {};
    if (filterCat !== "all") params.categorie = filterCat;
    if (q) params.q = q;
    const { data } = await api.get("/kb", { params });
    setRows(data);
  };
  useEffect(() => { load(); }, [filterCat]);

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const tags = form.tags_text.split(",").map((t) => t.trim()).filter(Boolean);
      const payload = { title: form.title, content: form.content, categorie: form.categorie, tags, published: form.published };
      if (editing) {
        await api.patch(`/kb/${editing.id}`, payload);
        toast.success("Article mis à jour");
      } else {
        await api.post("/kb", payload);
        toast.success("Article créé");
      }
      setOpen(false);
      setEditing(null);
      setForm(emptyForm);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur");
    } finally {
      setSaving(false);
    }
  };

  const startEdit = (a) => {
    setEditing(a);
    setForm({
      title: a.title || "",
      content: a.content || "",
      categorie: a.categorie || "general",
      tags_text: (a.tags || []).join(", "),
      published: a.published !== false,
    });
    setOpen(true);
  };

  const remove = async (id) => {
    if (!window.confirm("Supprimer cet article ?")) return;
    try {
      await api.delete(`/kb/${id}`);
      toast.success("Article supprimé");
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur");
    }
  };

  const runAsk = async (e) => {
    e?.preventDefault();
    if (!question.trim()) return;
    setAsking(true);
    setAnswer(null);
    try {
      const { data } = await api.post("/ai/ask", { question });
      setAnswer(data);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur IA");
    } finally {
      setAsking(false);
    }
  };

  const filtered = useMemo(() => {
    if (!q) return rows;
    const s = q.toLowerCase();
    return rows.filter((a) =>
      a.title?.toLowerCase().includes(s) ||
      a.content?.toLowerCase().includes(s) ||
      (a.tags || []).some((t) => t.toLowerCase().includes(s))
    );
  }, [rows, q]);

  return (
    <div>
      <PageHeader
        eyebrow="IA"
        title="Base de connaissances"
        description="Articles internes, procédures, FAQ — utilisés comme contexte par l'IA (Claude) pour analyser les réclamations."
        testid="kb-header"
        actions={
          <div className="flex items-center gap-2">
            <Button variant="outline" onClick={() => setAskOpen(true)} className="rounded-full" data-testid="kb-ask-btn">
              <Sparkles className="w-4 h-4 mr-2 text-violet-600" /> Demander à l&apos;IA
            </Button>
            {canWrite && (
              <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (!v) { setEditing(null); setForm(emptyForm); } }}>
                <DialogTrigger asChild>
                  <Button className="bg-blue-900 hover:bg-blue-800 rounded-full" data-testid="kb-create-btn">
                    <Plus className="w-4 h-4 mr-2" /> Nouvel article
                  </Button>
                </DialogTrigger>
                <DialogContent className="max-w-2xl">
                  <DialogHeader>
                    <DialogTitle className="font-display">{editing ? "Modifier l'article" : "Nouvel article"}</DialogTitle>
                    <DialogDescription>Rédigez un article pour nourrir l&apos;IA et guider les agents.</DialogDescription>
                  </DialogHeader>
                  <form onSubmit={submit} className="space-y-3">
                    <div className="space-y-1.5">
                      <Label>Titre</Label>
                      <Input required value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} data-testid="kb-title-input" />
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                      <div className="space-y-1.5">
                        <Label>Catégorie</Label>
                        <Select value={form.categorie} onValueChange={(v) => setForm({ ...form, categorie: v })}>
                          <SelectTrigger data-testid="kb-cat-select"><SelectValue /></SelectTrigger>
                          <SelectContent>{CATEGORIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
                        </Select>
                      </div>
                      <div className="space-y-1.5">
                        <Label>Tags (séparés par des virgules)</Label>
                        <Input value={form.tags_text} onChange={(e) => setForm({ ...form, tags_text: e.target.value })} placeholder="délai, remboursement..." data-testid="kb-tags-input" />
                      </div>
                    </div>
                    <div className="space-y-1.5">
                      <Label>Contenu</Label>
                      <Textarea required rows={10} value={form.content} onChange={(e) => setForm({ ...form, content: e.target.value })} data-testid="kb-content-input" placeholder="Procédure, réponse type, FAQ..." className="font-mono text-sm" />
                    </div>
                    <div className="flex items-center gap-2">
                      <Switch checked={form.published} onCheckedChange={(v) => setForm({ ...form, published: v })} data-testid="kb-published-switch" />
                      <Label className="cursor-pointer">Publié (utilisable par l&apos;IA)</Label>
                    </div>
                    <DialogFooter>
                      <Button type="button" variant="outline" onClick={() => setOpen(false)}>Annuler</Button>
                      <Button type="submit" disabled={saving} data-testid="kb-submit-btn">{editing ? "Enregistrer" : "Créer"}</Button>
                    </DialogFooter>
                  </form>
                </DialogContent>
              </Dialog>
            )}
          </div>
        }
      />

      <Card className="border border-slate-200 bg-white mb-4">
        <CardContent className="p-4">
          <div className="flex flex-col md:flex-row gap-3 md:items-center md:justify-between">
            <div className="relative flex-1 max-w-sm">
              <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
              <Input placeholder="Rechercher un article..." className="pl-9" value={q} onChange={(e) => setQ(e.target.value)} onKeyDown={(e) => e.key === "Enter" && load()} data-testid="kb-search-input" />
            </div>
            <Select value={filterCat} onValueChange={setFilterCat}>
              <SelectTrigger className="w-52" data-testid="kb-cat-filter"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Toutes catégories</SelectItem>
                {CATEGORIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {filtered.length === 0 ? (
          <div className="col-span-2 text-center py-16 text-slate-400 border border-slate-200 rounded-2xl bg-white">
            <BookOpen className="w-10 h-10 mx-auto mb-2 opacity-40" /> Aucun article
          </div>
        ) : filtered.map((a) => (
          <Card key={a.id} className="border border-slate-200 bg-white card-hover" data-testid={`kb-card-${a.id}`}>
            <CardContent className="p-5">
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2 mb-1.5 flex-wrap">
                    <Badge variant="outline" className="text-slate-500 capitalize">{a.categorie || "general"}</Badge>
                    {!a.published && <Badge variant="outline" className="text-orange-600 border-orange-200">Brouillon</Badge>}
                  </div>
                  <h3 className="font-display text-lg font-semibold text-slate-900 leading-snug">{a.title}</h3>
                  <p className="text-sm text-slate-600 mt-2 line-clamp-3 whitespace-pre-wrap">{a.content}</p>
                  {a.tags?.length > 0 && (
                    <div className="mt-3 flex flex-wrap gap-1.5">
                      {a.tags.map((t) => (
                        <span key={t} className="inline-flex items-center gap-1 text-[10px] font-mono bg-blue-50 text-blue-700 border border-blue-200 rounded-full px-2 py-0.5">
                          <Tag className="w-2.5 h-2.5" /> {t}
                        </span>
                      ))}
                    </div>
                  )}
                </div>
                {canWrite && (
                  <div className="flex flex-col gap-1 shrink-0">
                    <Button variant="ghost" size="sm" onClick={() => startEdit(a)} data-testid={`kb-edit-${a.id}`}>
                      <Pencil className="w-4 h-4" />
                    </Button>
                    <Button variant="ghost" size="sm" onClick={() => remove(a.id)} data-testid={`kb-delete-${a.id}`}>
                      <Trash2 className="w-4 h-4 text-red-500" />
                    </Button>
                  </div>
                )}
              </div>
              <div className="text-[11px] text-slate-400 mt-3 font-mono">
                Modifié {new Date(a.updated_at).toLocaleString("fr-FR")}
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* AI Ask dialog */}
      <Dialog open={askOpen} onOpenChange={setAskOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle className="font-display flex items-center gap-2">
              <Sparkles className="w-5 h-5 text-violet-600" /> Poser une question à l&apos;IA
            </DialogTitle>
            <DialogDescription>Claude répond en s&apos;appuyant strictement sur votre base de connaissances.</DialogDescription>
          </DialogHeader>
          <form onSubmit={runAsk} className="space-y-3">
            <Textarea
              rows={3}
              value={question}
              onChange={(e) => setQuestion(e.target.value)}
              placeholder="Ex: Quel est le délai de traitement d'une réclamation de type pension ?"
              data-testid="kb-ask-question"
            />
            <Button type="submit" disabled={asking} className="bg-violet-600 hover:bg-violet-700 text-white" data-testid="kb-ask-submit">
              <Send className="w-4 h-4 mr-2" /> {asking ? "Recherche..." : "Envoyer"}
            </Button>
          </form>
          {answer && (
            <div className="mt-4 space-y-2" data-testid="kb-answer">
              <Label className="text-xs text-slate-500">Réponse IA</Label>
              <div className="text-sm text-slate-800 whitespace-pre-wrap border border-violet-200 rounded-lg p-3 bg-violet-50/60">
                {answer.answer}
              </div>
              {answer.kb_used?.length > 0 && (
                <div className="text-xs text-slate-500">
                  Sources ({answer.kb_used.length}) :&nbsp;
                  {answer.kb_used.map((s) => s.title).join(" · ")}
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
